import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero, CTABand } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { ARTICLES } from "@/data/site";

function ArticleMeta({ article, className = "" }) {
  return (
    <p className={`font-mono text-[11px] uppercase tracking-[0.18em] ${className}`}>
      <span className="text-emerald-600">{article.category}</span>
      <span className="text-slate-400"> · {article.date} · {article.author}</span>
    </p>
  );
}

export default function News() {
  const [featured, ...rest] = ARTICLES;

  return (
    <>
      <Seo
        title="News & Insights — BNM Sustainable Energy Limited"
        description="News, announcements and insights from BNM Sustainable Energy Limited — clean-energy technology, projects and company updates from Sierra Leone."
      />
      <PageHero
        testid="news-hero"
        dark={false}
        eyebrow="News & Insights"
        lines={["The BNM", "journal."]}
        lead="Announcements, engineering updates and perspectives on clean energy in Sierra Leone and Africa — from the BNM team."
      />

      <section data-testid="news-grid" className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal>
            <Link
              to={`/news/${featured.slug}`}
              data-testid={`news-article-card-${featured.slug}`}
              className="group block rounded-lg border border-slate-200 bg-white overflow-hidden transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-emerald-300 hover:shadow-xl"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <span className="block overflow-hidden">
                  <img
                    src={featured.image}
                    alt={featured.imageAlt}
                    loading="lazy"
                    width="1024"
                    height="768"
                    className="w-full h-72 lg:h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </span>
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <ArticleMeta article={featured} />
                  <h2 className="mt-4 font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 leading-snug text-balance group-hover:text-emerald-800 transition-colors">
                    {featured.title}
                  </h2>
                  <p className="mt-4 text-base leading-relaxed text-slate-600">{featured.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-2 font-semibold text-emerald-700 group-hover:text-emerald-900 transition-colors">
                    Read the story
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </span>
                </div>
              </div>
            </Link>
          </Reveal>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
            {rest.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.08}>
                <Link
                  to={`/news/${a.slug}`}
                  data-testid={`news-article-card-${a.slug}`}
                  className="group flex flex-col h-full rounded-lg border border-slate-200 bg-white overflow-hidden transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-emerald-300 hover:shadow-xl"
                >
                  <span className="block overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.imageAlt}
                      loading="lazy"
                      width="1024"
                      height="768"
                      className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </span>
                  <div className="p-7 flex flex-col flex-1">
                    <ArticleMeta article={a} />
                    <h2 className="mt-3 font-display text-xl font-bold tracking-tight text-slate-900 leading-snug group-hover:text-emerald-800 transition-colors">
                      {a.title}
                    </h2>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{a.excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 group-hover:text-emerald-900 transition-colors">
                      Read the story
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        title="Media or press inquiry?"
        text="Journalists and media organisations can reach the BNM team directly."
        primary={{ label: "Contact the Team", to: "/contact", testid: "news-contact-cta" }}
        testid="news-cta"
      />
    </>
  );
}
